'use client';

import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { Radio } from '@packages/shared/src/ui/Radio/Radio';
import { RadioGroup } from '@packages/shared/src/ui/RadioGroup/RadioGroup';
import { Textarea } from '@packages/shared/src/ui/Textarea/Textarea';
import { RatingInput } from '../RatingInput/RatingInput.client';
import { CustomerReviewTerms } from '../CustomerReviewTerms/CustomerReviewTerms.client';
import { addReviewFormMessages } from './AddReviewForm.messages';
import type { CreateReviewFormSchema } from '../AddReviewFormValidation/AddReviewFormValidation';
import { validationErrors } from '../AddReviewFormValidation/AddReviewFormValidation';

/**
 * First step with form in rating creation dialog
 */
export const AddReviewForm = () => {
  const { formatMessage } = useIntl();
  const {
    control,
    formState: { errors },
    register,
  } = useFormContext<CreateReviewFormSchema>();

  return (
    <div className="flex flex-col gap-2.5">
      <input {...register('productId')} type="hidden" />
      <RatingInput />
      <Controller
        control={control}
        name="recommended"
        render={({ field }) => (
          <RadioGroup label={formatMessage(addReviewFormMessages.recommendedLabel)}>
            <div className="flex flex-row items-center gap-4 pt-2">
              <Radio
                label={formatMessage(addReviewFormMessages.recommendedYes)}
                checked={field.value === true}
                onChange={() => field.onChange(true)}
              />
              <Radio
                label={formatMessage(addReviewFormMessages.recommendedNo)}
                checked={field.value === false}
                onChange={() => field.onChange(false)}
              />
            </div>
          </RadioGroup>
        )}
      />
      <Input
        {...register('title')}
        label={formatMessage(addReviewFormMessages.titleLabel)}
        placeholder={formatMessage(addReviewFormMessages.titlePlaceholder)}
      />
      <Textarea
        {...register('description')}
        label={formatMessage(addReviewFormMessages.descriptionLabel)}
        placeholder={formatMessage(addReviewFormMessages.descriptionPlaceholder)}
        helperText={
          errors.description && formatMessage(addReviewFormMessages.descriptionErrorTooShort)
        }
        error={!!errors.description}
        className="min-h-[6rem]"
        required
      />
      <Input
        {...register('name')}
        label={formatMessage(addReviewFormMessages.nameLabel)}
        placeholder={formatMessage(addReviewFormMessages.namePlaceholder)}
        helperText={
          errors.name &&
          formatMessage(
            errors.name?.message === validationErrors.tooShort
              ? addReviewFormMessages.nameErrorTooShort
              : addReviewFormMessages.nameErrorTooLong,
          )
        }
        error={!!errors.name}
      />
      <CustomerReviewTerms />
      <p className="text-size-body-sm text-color-text-darktransparent">
        {formatMessage(addReviewFormMessages.mandatoryHint)}
      </p>
    </div>
  );
};
